<template>
  <section>
    <v-container>
      <v-row justify="start">
        <v-toolbar-title
          class="grey--text text--darken-4 font-weight-black my-5 ml-12"
        >
          Daily Medication Schedule - {{ currentDate }}
        </v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Search medications or residents"
          single-line
          hide-details
        >
        </v-text-field>
      </v-row>

      <!-- Today's Scheduled Medications -->
      <v-card class="elevation-2">
        <v-card-title>
          <v-icon left color="primary">schedule</v-icon>
          Today's Scheduled Medications ({{ filteredMedications.length }})
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="filteredMedications"
          :loading="loading"
          loading-text="Loading today's medications..."
          :items-per-page="-1"
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:item.scheduleTime="{ item }">
            <v-chip
              :color="getTimeChipColor(item.scheduleTime)"
              text-color="white"
              small
            >
              <v-icon left small>access_time</v-icon>
              {{ item.scheduleTime || 'No time set' }}
            </v-chip>
          </template>

          <template v-slot:item.type="{ item }">
            <v-chip
              :color="getTypeChipColor(item.type)"
              text-color="white"
              small
            >
              {{ item.type || 'Normal' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="administerMedication(item)"
                >
                  <v-icon>medication</v-icon>
                </v-btn>
              </template>
              <span>Administer Medication</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                  @click="viewDetails(item)"
                >
                  <v-icon>info</v-icon>
                </v-btn>
              </template>
              <span>View Details</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-4">
              <v-icon size="64" color="grey lighten-1">medication</v-icon>
              <div class="text-h6 grey--text mt-2">No medications scheduled for today</div>
              <div class="text-body-2 grey--text">All caught up! 🎉</div>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Quick Stats -->
      <v-row class="mt-4">
        <v-col cols="12" sm="6" md="3">
          <v-card color="primary" dark>
            <v-card-text>
              <div class="text-h4">{{ filteredMedications.length }}</div>
              <div>Total Scheduled</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="success" dark>
            <v-card-text>
              <div class="text-h4">{{ morningMedications.length }}</div>
              <div>Morning (6AM-12PM)</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="warning" dark>
            <v-card-text>
              <div class="text-h4">{{ afternoonMedications.length }}</div>
              <div>Afternoon (12PM-6PM)</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="info" dark>
            <v-card-text>
              <div class="text-h4">{{ eveningMedications.length }}</div>
              <div>Evening (6PM-12AM)</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import medicationApi from '../../services/api/MedicationApi'
import moment from 'moment'

export default {
  name: 'DailyMedicationSchedule',
  data() {
    return {
      loading: true,
      medications: [],
      search: '',
      currentDay: '',
      headers: [
        { text: 'Medication Name', value: 'name', class: 'success--text font-weight-bold' },
        { text: 'Resident', value: 'resident.fullName', class: 'success--text font-weight-bold' },
        { text: 'Description', value: 'description', sortable: false, class: 'success--text font-weight-bold' },
        { text: 'Schedule Time', value: 'scheduleTime', class: 'success--text font-weight-bold' },
        { text: 'Type', value: 'type', class: 'success--text font-weight-bold' },
        { text: 'Actions', value: 'actions', sortable: false, class: 'success--text font-weight-bold' },
      ],
    }
  },
  computed: {
    currentDate() {
      return moment().format('dddd, MMMM Do YYYY')
    },
    currentDayOfWeek() {
      return moment().format('dddd').toLowerCase()
    },
    filteredMedications() {
      if (!this.medications.length) return []
      
      // Filter medications scheduled for today
      let todayMedications = this.medications.filter(medication => {
        if (!medication.schedule) return false
        return medication.schedule[this.currentDayOfWeek] === true
      })

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        todayMedications = todayMedications.filter(medication => 
          medication.name.toLowerCase().includes(searchLower) ||
          medication.resident.fullName.toLowerCase().includes(searchLower) ||
          (medication.description && medication.description.toLowerCase().includes(searchLower))
        )
      }

      return todayMedications
    },
    morningMedications() {
      return this.filteredMedications.filter(med => this.isMorningTime(med.scheduleTime))
    },
    afternoonMedications() {
      return this.filteredMedications.filter(med => this.isAfternoonTime(med.scheduleTime))
    },
    eveningMedications() {
      return this.filteredMedications.filter(med => this.isEveningTime(med.scheduleTime))
    }
  },
  methods: {
    ...mapActions([
      'getLoggedInUser',
    ]),
    async getTodaysMedications() {
      this.loading = true
      try {
        // Get all medications and filter by current day
        const response = await medicationApi.getAllMedication({
          page: 1,
          count: 1000, // Get all medications
          id: '',
          q: '',
          orderBy: 'name',
          order: 'asc'
        })
        
        this.medications = response.result || []
      } catch (error) {
        console.error('Error fetching medications:', error)
        this.medications = []
      } finally {
        this.loading = false
      }
    },
    isMorningTime(time) {
      if (!time) return false
      const hour = parseInt(time.split(':')[0])
      return hour >= 6 && hour < 12
    },
    isAfternoonTime(time) {
      if (!time) return false
      const hour = parseInt(time.split(':')[0])
      return hour >= 12 && hour < 18
    },
    isEveningTime(time) {
      if (!time) return false
      const hour = parseInt(time.split(':')[0])
      return hour >= 18 || hour < 6
    },
    getTimeChipColor(time) {
      if (!time) return 'grey'
      const hour = parseInt(time.split(':')[0])
      if (hour >= 6 && hour < 12) return 'success'
      if (hour >= 12 && hour < 18) return 'warning'
      return 'info'
    },
    getTypeChipColor(type) {
      if (!type) return 'primary'
      const typeLower = type.toLowerCase()
      if (typeLower.includes('control')) return 'error'
      if (typeLower.includes('schedule')) return 'orange'
      return 'primary'
    },
    administerMedication(medication) {
      // Navigate to medication administration for this specific medication
      this.$router.push({
        name: 'registerMedicationAdministration',
        params: { residentId: medication.resident.id },
        query: { medicationId: medication.id }
      })
    },
    viewDetails(medication) {
      // Navigate to medication details
      this.$router.push({
        name: 'medicationDetail',
        params: {
          medicationId: medication.id,
          residentId: medication.resident.id
        }
      })
    }
  },
  async created() {
    await this.getLoggedInUser()
    await this.getTodaysMedications()
  }
}
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}

.v-chip {
  margin: 2px;
}

.text-h4 {
  font-weight: bold;
}
</style>
